/**
 * Cognitive Bias Audit Page
 *
 * A dedicated workspace for leaders to run AI-assisted cognitive bias
 * detection across their current cycle or session context. Shows each
 * detected bias with a concrete illustration of how it manifests in
 * their actual prioritization work.
 *
 * Accessible via Reports → Bias Audit in the sidebar.
 */

import { useState, useMemo } from 'react';
import { Brain, RefreshCw, Info, ChevronDown, ChevronUp, Sparkles, BookOpen } from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { detectCognitiveBiases } from '../../../lib/llmCognitiveBiasService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import { BiasInsightCard } from '../../../components/prioritization/insights/BiasInsightCard';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import type {
  BiasAnalysisContext,
  BiasDetectionResult,
  BiasCategory,
  BiasSeverity,
} from '../../../types/prioritization/cognitiveBias';
import { BIAS_CATEGORY_META } from '../../../types/prioritization/cognitiveBias';

// ── Helpers ───────────────────────────────────────────────────────────────────

function sessionUpdatedAtMs(updatedAt: Date | string | null | undefined): number {
  if (!updatedAt) return 0;
  if (updatedAt instanceof Date) return updatedAt.getTime();
  const parsed = new Date(updatedAt).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

const RISK_BANNER: Record<string, { bg: string; text: string; label: string }> = {
  high:   { bg: 'bg-[var(--coral-dim)] border-[var(--coral)]',    text: 'text-[var(--coral)]',    label: 'High Bias Risk' },
  medium: { bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]', text: 'text-[var(--mustard)]',  label: 'Medium Bias Risk' },
  low:    { bg: 'bg-[var(--slate-dim)] border-[var(--slate)]',   text: 'text-[var(--text-sec)]',   label: 'Low Bias Risk' },
  none:   { bg: 'bg-[var(--green-dim)] border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]',  label: 'No Significant Biases Detected' },
};

function ScopeSelector({
  scope,
  onChange,
}: {
  scope: 'session' | 'cycle' | 'portfolio';
  onChange: (s: 'session' | 'cycle' | 'portfolio') => void;
}) {
  const options: Array<{ value: 'session' | 'cycle' | 'portfolio'; label: string; description: string }> = [
    { value: 'session', label: 'Most Recent Session', description: 'Analyze the last completed prioritization session' },
    { value: 'cycle',   label: 'Active Cycle',        description: 'Analyze all items in the current cycle' },
    { value: 'portfolio', label: 'Full Portfolio',    description: 'Analyze your entire active item portfolio' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`text-left  border p-4 transition-all ${
            scope === opt.value
              ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-2 ring-indigo-100'
              : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
          }`}
        >
          <p className={`text-sm font-semibold ${scope === opt.value ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
            {opt.label}
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">{opt.description}</p>
        </button>
      ))}
    </div>
  );
}

function CategorySummary({ result }: { result: BiasDetectionResult }) {
  const byCategory: Partial<Record<BiasCategory, number>> = {};
  for (const bias of result.detectedBiases) {
    const { category } = { category: 'cognitive_shortcuts' as BiasCategory, ...{ category: getCategoryForType(bias.biasType) } };
    byCategory[category] = (byCategory[category] ?? 0) + 1;
  }

  if (Object.keys(byCategory).length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {(Object.keys(byCategory) as BiasCategory[]).map(cat => {
        const meta = BIAS_CATEGORY_META[cat];
        return (
          <span
            key={cat}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium
              ${cat === 'group_dynamics'      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]' : ''}
              ${cat === 'cognitive_shortcuts' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]' : ''}
              ${cat === 'strategic_framing'   ? 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]' : ''}
              ${cat === 'confidence'          ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]' : ''}
            `}
          >
            {meta.label}
            <span className="font-bold">{byCategory[cat]}</span>
          </span>
        );
      })}
    </div>
  );
}

function getCategoryForType(biasType: string): BiasCategory {
  const map: Record<string, BiasCategory> = {
    hippo_effect: 'group_dynamics', groupthink: 'group_dynamics', bandwagon_effect: 'group_dynamics',
    recency_bias: 'cognitive_shortcuts', availability_heuristic: 'cognitive_shortcuts',
    anchoring_bias: 'cognitive_shortcuts', confirmation_bias: 'cognitive_shortcuts',
    sunk_cost_fallacy: 'strategic_framing', status_quo_bias: 'strategic_framing', narrative_fallacy: 'strategic_framing',
    optimism_bias: 'confidence', planning_fallacy: 'confidence',
  };
  return map[biasType] ?? 'cognitive_shortcuts';
}

function KahnemanCallout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--surface)] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-[var(--mustard-dim)] flex items-center justify-center">
            <BookOpen className="h-4 w-4 text-[var(--mustard)]" />
          </div>
          <span className="text-sm font-semibold text-[var(--text-pri)]">Why this matters — the Kahneman insight</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-[var(--rule)] space-y-3">
          <p className="text-sm text-[var(--text-sec)] leading-relaxed">
            Daniel Kahneman — who wrote <em>Thinking, Fast and Slow</em> and won the Nobel Prize for his work on
            decision biases — has said that he himself does not know when he is engaging in a cognitive bias.
            Knowing the name of a bias does not protect you from it.
          </p>
          <p className="text-sm text-[var(--text-sec)] leading-relaxed">
            System 1 (fast, intuitive thinking) generates the bias. System 2 (slow, deliberate thinking) is the
            only thing that can catch it — but only if it is alerted to look. That is what this tool does:
            it alerts System 2 to patterns that System 1 has already acted on.
          </p>
          <p className="text-sm text-[var(--text-sec)] leading-relaxed">
            Business leaders who have never studied Kahneman are just as susceptible as those who have.
            The difference is whether your process has a mechanism to surface the pattern before the outcome is locked.
          </p>
          <div className="flex items-start gap-2 pt-1 bg-[var(--mustard-dim)] px-4 py-3">
            <Info className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--mustard)] leading-relaxed">
              This analysis is advisory only. AI can identify statistical patterns that suggest a bias is operating —
              but it cannot determine intent or context. Use the findings as prompts for reflection and conversation,
              not as verdicts.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function CognitiveBiasPage() {
  const cognitiveBiasDetectorEnabled = useAiCapability('cognitive_bias_detector');
  const items = useItemsStore(state => state.items);
  const sessions = useSessionStore(state => state.sessions);
  const cycles = useDegapProcessStore(state => state.cycles);
  const tracks = useTrackStore(state => state.tracks);
  const userId = useUserStore(state => state.userId) ?? '';

  const [scope, setScope] = useState<'session' | 'cycle' | 'portfolio'>('session');
  const [result, setResult] = useState<BiasDetectionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build context based on selected scope
  const analysisContext = useMemo((): BiasAnalysisContext | null => {
    const activeItems = items.filter(i => i.state !== 'archived');
    if (activeItems.length === 0) return null;

    if (scope === 'session') {
      const completedSession = sessions
        .filter(s => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED')
        .sort((a, b) => sessionUpdatedAtMs(b.updatedAt) - sessionUpdatedAtMs(a.updatedAt))
        .at(0);

      if (!completedSession) return null;

      const rankedIds = completedSession.rankingOutput?.rankedItemIds ?? [];
      const cycle = cycles.find(c => c.id === completedSession.cycleId);

      return {
        analysisScope: 'session',
        sessionName: completedSession.name ?? 'Session',
        sessionTool: completedSession.selectedToolId ?? undefined,
        cycleName: cycle?.name,
        items: rankedIds.map((id, idx) => {
          const item = items.find(i => i.id === id);
          return {
            id,
            title: item?.title ?? id,
            category: (item as unknown as Record<string, string>)?.itemLanguageType,
            bucket: (item as unknown as Record<string, string>)?.strategicBucket,
            rank: idx + 1,
          };
        }),
        participants: (completedSession.participantIds ?? []).map(pid => ({
          id: pid,
        })),
        criteriaLabels: completedSession.criteriaConfig?.criteriaIds ?? [],
        cutLinePosition: completedSession.cutLinePosition ?? undefined,
        workspaceName: completedSession.workspaceId ?? undefined,
      };
    }

    if (scope === 'cycle') {
      const activeCycle = cycles.find(c => c.state === 'active' || c.state === 'in_progress') ?? cycles[0];
      if (!activeCycle) return null;

      const cycleItems = activeItems.filter(i =>
        (i as unknown as Record<string, unknown>).cycleId === activeCycle.id
      );
      const fallbackItems = cycleItems.length > 0 ? cycleItems : activeItems.slice(0, 30);

      const cycleTracks = tracks.filter(t => t.cycleId === activeCycle.id);

      return {
        analysisScope: 'cycle',
        cycleName: activeCycle.name,
        items: fallbackItems.map(item => ({
          id: item.id,
          title: item.title,
          category: (item as unknown as Record<string, string>)?.itemLanguageType,
          bucket: (item as unknown as Record<string, string>)?.strategicBucket,
        })),
        participants: cycleTracks.flatMap(t =>
          ((t as unknown as Record<string, unknown[]>).participantIds ?? []).map((pid: unknown) => ({
            id: String(pid),
          }))
        ).slice(0, 20),
        workspaceName: activeCycle.workspaceId ?? undefined,
      };
    }

    // portfolio
    return {
      analysisScope: 'portfolio',
      items: activeItems.slice(0, 30).map(item => ({
        id: item.id,
        title: item.title,
        category: (item as unknown as Record<string, string>)?.itemLanguageType,
        bucket: (item as unknown as Record<string, string>)?.strategicBucket,
        createdAt: item.createdAt,
      })),
      participants: [{ id: userId }],
    };
  }, [scope, items, sessions, cycles, tracks, userId]);

  const handleRun = async () => {
    if (!analysisContext) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const detection = await detectCognitiveBiases(analysisContext);
      if (detection) {
        setResult(detection);
      } else {
        setError('There are no items to analyze in this scope.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const riskBanner = result ? RISK_BANNER[result.overallRiskLevel] ?? RISK_BANNER.none : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <PageHeader
        icon={<Brain className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Cognitive Bias Audit"
        subtitle="AI-assisted analysis of decision patterns that may reflect known cognitive biases"
      />

      <KahnemanCallout />

      {/* Scope selector */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 space-y-4">
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)] mb-1">What to analyze</p>
          <p className="text-xs text-[var(--text-ter)]">Choose the scope of the bias scan.</p>
        </div>
        <ScopeSelector scope={scope} onChange={s => { setScope(s); setResult(null); }} />

        {!analysisContext && (
          <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3">
            <p className="text-sm text-[var(--mustard)]">
              {scope === 'session'
                ? 'No completed sessions found. Run and complete a prioritization session first.'
                : scope === 'cycle'
                ? 'No active cycles found. Start a cycle to analyze it.'
                : 'No active items found in your portfolio.'}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-[var(--text-ter)]">
            {analysisContext
              ? `${analysisContext.items.length} items · ${analysisContext.participants.length} participants`
              : 'No data available for this scope'}
          </p>
          {cognitiveBiasDetectorEnabled && (
          <button
            type="button"
            onClick={handleRun}
            disabled={loading || !analysisContext}
            className="inline-flex items-center gap-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:bg-[var(--hover-tint)] disabled:text-[var(--text-ter)] text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {result ? 'Re-run Analysis' : 'Run Bias Analysis'}
              </>
            )}
          </button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-10 flex flex-col items-center gap-4 text-center">
          <div className="w-12 h-12 rounded-full bg-[var(--slate-dim)] flex items-center justify-center animate-pulse">
            <Brain className="h-6 w-6 text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[var(--text-sec)]">Scanning for decision patterns…</p>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              Checking for groupthink, recency bias, sunk costs, HiPPO effect, and more
            </p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-[var(--bg-raised)] border border-[var(--coral)] px-5 py-4">
          <p className="text-sm font-semibold text-[var(--coral)]">Analysis failed</p>
          <p className="text-xs text-[var(--coral)] mt-1">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div className="space-y-4">
          {/* Risk banner */}
          {riskBanner && (
            <div className={` border px-5 py-4 ${riskBanner.bg}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm font-semibold ${riskBanner.text}`}>{riskBanner.label}</p>
                  {result.summary && (
                    <p className={`text-sm mt-1 leading-relaxed ${riskBanner.text} opacity-80`}>
                      {result.summary}
                    </p>
                  )}
                </div>
                {result.detectedBiases.length > 0 && (
                  <div className="shrink-0 ml-4">
                    <CategorySummary result={result} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Biases */}
          {result.detectedBiases.length === 0 ? (
            <div className="bg-[var(--bg-raised)] border border-[var(--green)] px-5 py-6 text-center">
              <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">No significant bias patterns detected</p>
              <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
                The scoring patterns and group dynamics in this context look well-balanced.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide px-1">
                Detected Biases — {result.detectedBiases.length} found
              </p>
              {/* Sort high → medium → low */}
              {(['high', 'medium', 'low'] as BiasSeverity[]).flatMap(sev =>
                result.detectedBiases
                  .filter(b => b.severity === sev)
                  .map(bias => (
                    <BiasInsightCard
                      key={bias.biasType}
                      bias={bias}
                      defaultExpanded={sev === 'high'}
                    />
                  ))
              )}
            </div>
          )}

          <p className="text-[11px] text-[var(--text-ter)] text-center pt-2">
            Generated {new Date(result.generatedAt).toLocaleString()} · Advisory only — not a verdict · Re-run as context evolves
          </p>
        </div>
      )}
    </div>
  );
}

export default CognitiveBiasPage;
